import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import {
  SectionCard,
  Divider,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  FieldInput,
  Tile
} from "@/components/ds";
import { FileText } from "@/components/ds/icons";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import QuickBooksDataMapping from "./QuickBooksDataMapping";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import { ensureDefaultRoutingWorkflow } from "@/pages/Messaging/Workflows/defaultWorkflowHelper";
const QB_COLOR = "#2CA01C";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingQuickBooks() {
  const navigate = useNavigate();
  return (
    <GhostButton
      onClick={() => navigate("/integrations")}
      className="!text-base !font-normal !text-zinc-600 hover:!text-zinc-800 hover:!no-underline !gap-2"
    >
      <svg
        viewBox="0 0 20 20"
        width="16"
        height="16"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </GhostButton>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface QuickBooksConfig {
  id: number;
  siteId: number;
  displayName: string;
  clientIdMasked: string;
  realmId: string;
  enabled: boolean;
  companyName?: string;
  createdAt: string;
  pushTranscripts: boolean;
}

type TabId =
  | "overview"
  | "accounts"
  | "ai-tools"
  | "field-reference"
  | "data-mapping"
  | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Customer lookup",
    icon: "User",
    desc: "Find any customer by email or name, see open invoices and payment history"
  },
  {
    title: "Invoice management",
    icon: "FileText",
    desc: "Create, send, and void invoices directly from the chat"
  },
  {
    title: "Payment recording",
    icon: "CreditCard",
    desc: "Log payments, check outstanding balances, view payment history"
  },
  {
    title: "Estimates & quotes",
    icon: "FileText",
    desc: "Create estimates and convert them to invoices in one conversation"
  },
  {
    title: "Financial reporting",
    icon: "BarChart2",
    desc: "Pull AR aging, sales summaries, and company info during support chats"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  {
    name: "qb_get_customer",
    label: "Get customer",
    group: "Customers",
    description:
      "Retrieve a customer by ID — name, email, phone, billing address, and balance."
  },
  {
    name: "qb_search_customers",
    label: "Search customers",
    group: "Customers",
    description:
      "Find customers by name or email. Returns list with open balance and contact info."
  },
  {
    name: "qb_create_customer",
    label: "Create customer",
    group: "Customers",
    description:
      "Add a new customer record with name, email, phone, and billing address."
  },
  {
    name: "qb_update_customer",
    label: "Update customer",
    group: "Customers",
    description: "Update customer fields — email, phone, address, or notes."
  },

  {
    name: "qb_get_invoice",
    label: "Get invoice",
    group: "Invoices",
    description:
      "Full invoice details: line items, amounts, due date, status, and customer info."
  },
  {
    name: "qb_search_invoices",
    label: "Search invoices",
    group: "Invoices",
    description:
      "Find invoices by customer, date range, or status. Returns totals and due dates."
  },
  {
    name: "qb_create_invoice",
    label: "Create invoice",
    group: "Invoices",
    description:
      "Create a new invoice with line items, quantities, and due date for a customer."
  },
  {
    name: "qb_send_invoice",
    label: "Send invoice",
    group: "Invoices",
    description: "Email an invoice to the customer directly from QuickBooks."
  },
  {
    name: "qb_void_invoice",
    label: "Void invoice",
    group: "Invoices",
    description:
      "Void an existing invoice — keeps the record but zeroes the amount."
  },

  {
    name: "qb_get_payment",
    label: "Get payment",
    group: "Payments",
    description:
      "Retrieve a payment record with amount, date, method, and applied invoices."
  },
  {
    name: "qb_list_payments",
    label: "List payments",
    group: "Payments",
    description:
      "All payments for a customer — amounts, dates, and applied invoice references."
  },
  {
    name: "qb_create_payment",
    label: "Create payment",
    group: "Payments",
    description:
      "Record a new payment against one or more invoices for a customer."
  },
  {
    name: "qb_record_payment",
    label: "Record payment",
    group: "Payments",
    description:
      "Log a payment received via check, ACH, or credit card with memo."
  },

  {
    name: "qb_get_estimate",
    label: "Get estimate",
    group: "Estimates",
    description:
      "Retrieve an estimate with line items, totals, expiry date, and status."
  },
  {
    name: "qb_create_estimate",
    label: "Create estimate",
    group: "Estimates",
    description:
      "Create a new estimate (quote) for a customer with line items and pricing."
  },
  {
    name: "qb_convert_estimate_to_invoice",
    label: "Convert estimate to invoice",
    group: "Estimates",
    description: "Convert an accepted estimate into a live invoice in one step."
  },

  {
    name: "qb_get_item",
    label: "Get item",
    group: "Items & Reports",
    description:
      "Retrieve a product or service item — name, price, description, and type."
  },
  {
    name: "qb_list_items",
    label: "List items",
    group: "Items & Reports",
    description: "All products and services in the QuickBooks item catalog."
  },
  {
    name: "qb_get_sales_report",
    label: "Get sales report",
    group: "Items & Reports",
    description:
      "Sales summary by date range — total revenue, top items, and customer breakdown."
  },
  {
    name: "qb_get_ar_report",
    label: "Get AR aging report",
    group: "Items & Reports",
    description:
      "Accounts receivable aging — outstanding invoices grouped by days overdue."
  },
  {
    name: "qb_get_company_info",
    label: "Get company info",
    group: "Items & Reports",
    description:
      "Company name, address, fiscal year start, and industry from QBO settings."
  }
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const QB_FIELD_REFERENCE = [
  {
    scope: "Customer",
    scopeKey: "customer",
    fields: [
      { field: "Id", label: "Customer ID", notes: "QBO internal ID" },
      {
        field: "DisplayName",
        label: "Display Name",
        notes: "Full customer name"
      },
      {
        field: "PrimaryEmailAddr.Address",
        label: "Email",
        notes: "Primary email address"
      },
      {
        field: "PrimaryPhone.FreeFormNumber",
        label: "Phone",
        notes: "Primary phone number"
      },
      {
        field: "Balance",
        label: "Balance Due",
        notes: "Total outstanding balance"
      },
      { field: "BillAddr.City", label: "City", notes: "Billing city" },
      { field: "Active", label: "Active", notes: "true / false" }
    ]
  },
  {
    scope: "Invoice",
    scopeKey: "invoice",
    fields: [
      { field: "Id", label: "Invoice ID", notes: "QBO internal ID" },
      {
        field: "DocNumber",
        label: "Invoice Number",
        notes: "Customer-facing invoice number"
      },
      { field: "TxnDate", label: "Invoice Date", notes: "YYYY-MM-DD" },
      { field: "DueDate", label: "Due Date", notes: "YYYY-MM-DD" },
      {
        field: "TotalAmt",
        label: "Total Amount",
        notes: "Total invoice amount"
      },
      {
        field: "Balance",
        label: "Amount Due",
        notes: "Remaining unpaid amount"
      },
      {
        field: "EmailStatus",
        label: "Email Status",
        notes: "NeedToSend / EmailSent / NotSet"
      }
    ]
  },
  {
    scope: "Payment",
    scopeKey: "payment",
    fields: [
      { field: "Id", label: "Payment ID", notes: "QBO internal ID" },
      { field: "TxnDate", label: "Payment Date", notes: "YYYY-MM-DD" },
      { field: "TotalAmt", label: "Amount", notes: "Payment amount" },
      {
        field: "PaymentMethodRef.name",
        label: "Payment Method",
        notes: "Check / Cash / Credit Card / etc."
      },
      { field: "PrivateNote", label: "Memo", notes: "Internal payment note" }
    ]
  }
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ok */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${copied ? "border-green-300 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
    </svg>
  );
}

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-zinc-50 hover:bg-zinc-100 text-sm font-medium text-zinc-700"
        aria-expanded={open}
      >
        <span>Where to find your QuickBooks credentials</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 text-sm text-zinc-600 space-y-1.5">
          <ol className="list-none space-y-2">
            {[
              "Go to developer.intuit.com and sign in",
              "Create a new app (or open an existing one) → get Client ID and Client Secret",
              "Run the OAuth 2.0 flow to get your Access Token and Refresh Token",
              "Find your Company ID (RealmId) in app.qbo.intuit.com → Settings → Account and Settings → Company ID"
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: QB_COLOR }}
                >
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ─── CapabilitiesPanel ────────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-zinc-200 mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} feature areas · 20 AI tools
          </span>
        </div>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="px-6 pb-6">
          <Divider />
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>All 20 AI tools activate automatically on connect</strong> —
            no extra configuration required.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-zinc-600">
                    <IconByName name={cap.icon} className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-medium text-zinc-700">
                    {cap.title}
                  </span>
                </div>
                <p className="text-xs text-zinc-500">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HowItWorksCard ───────────────────────────────────────────────────────────

function HowItWorksCard() {
  return (
    <SectionCard
      title="How QuickBooks works in Velaro"
      description="Connect once — every customer, invoice, and payment becomes AI-searchable instantly."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
              style={{ backgroundColor: QB_COLOR }}
            >
              1
            </span>
            <span className="font-semibold text-zinc-700 text-sm">
              Connect QuickBooks
            </span>
          </div>
          <p className="text-xs text-zinc-500">
            Enter your Client ID, Secret, RealmId, and OAuth tokens from
            developer.intuit.com.
          </p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
              2
            </span>
            <span className="font-semibold text-green-800 text-sm">
              20 AI tools activate
            </span>
          </div>
          <p className="text-xs text-green-700">
            Customers, invoices, payments, estimates, and reports — all
            AI-accessible in real time.
          </p>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
              <path
                d="M3 2L7 5L3 8"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
              3
            </span>
            <span className="font-semibold text-blue-800 text-sm">
              AI-powered billing support
            </span>
          </div>
          <p className="text-xs text-blue-700">
            Agents answer billing questions, create invoices, and record
            payments without leaving the conversation.
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  hasConnection,
  onConnect,
  onOpenWizard
}: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard
          title="Connect your QuickBooks account"
          description="Enter your OAuth credentials and every customer, invoice, and payment becomes instantly searchable by the AI."
          action={
            <PrimaryButton
              onClick={onConnect}
              className="shrink-0"
              style={{ backgroundColor: QB_COLOR }}
            >
              Connect Now →
            </PrimaryButton>
          }
        />
      ) : (
        <SectionCard
          title="Launch Starter Workflow Wizard"
          description="Build a billing support bot in minutes — invoice lookup, payment status, or AR reporting."
          action={
            <PrimaryButton
              onClick={onOpenWizard}
              className="shrink-0"
              style={{ backgroundColor: QB_COLOR }}
            >
              Launch Wizard →
            </PrimaryButton>
          }
        />
      )}
    </div>
  );
}

// ─── AiToolsTab ──────────────────────────────────────────────────────────────

function AiToolsTab({
  hasConnection,
  onOpenWizard
}: {
  hasConnection: boolean;
  onOpenWizard(): void;
}) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI Tools"
      description="All tools activate automatically on connect — no configuration required."
      action={
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
          >
            {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
          </span>
          {hasConnection && <LaunchWizardButton onClick={onOpenWizard} />}
        </div>
      }
    >
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <Tile
                  key={tool.name}
                  density="default"
                  className="!items-start !bg-zinc-50"
                >
                  <div
                    className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                    style={{ backgroundColor: QB_COLOR }}
                  />
                  <div>
                    <div className="text-xs font-semibold text-zinc-700">
                      {tool.label}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-400 mb-1">
                      {tool.name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {tool.description}
                    </div>
                  </div>
                </Tile>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Divider />
        <div className="pt-8">
          <SkillControlsPanel integration="quickbooks" skills={AI_TOOLS} />
        </div>
      </div>
    </SectionCard>
  );
}

// ─── AccountsTab ──────────────────────────────────────────────────────────────

interface QBAddFormState {
  displayName: string;
  clientId: string;
  clientSecret: string;
  realmId: string;
  accessToken: string;
  refreshToken: string;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

function AccountsTab({
  configs,
  onRefresh,
  onTogglePushTranscripts,
  pushTranscriptsError
}: {
  configs: QuickBooksConfig[];
  onRefresh(): void;
  onTogglePushTranscripts(config: QuickBooksConfig): void;
  pushTranscriptsError: string | null;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<QBAddFormState>({
    displayName: "",
    clientId: "",
    clientSecret: "",
    realmId: "",
    accessToken: "",
    refreshToken: "",
    saving: false,
    testing: false,
    error: null,
    testResult: null
  });

  function setField(field: keyof QBAddFormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleDelete(id: number) {
    if (!confirm("Remove this QuickBooks connection?")) return;
    setDeleting(id);
    try {
      await api.messaging.delete(`QuickBooks/Configs/${id}`);
      onRefresh();
    } catch (ex) {
      console.error("QuickBooks delete failed", ex);
    } finally {
      setDeleting(null);
    }
  }

  async function handleSave() {
    if (
      !form.clientId.trim() ||
      !form.clientSecret.trim() ||
      !form.realmId.trim()
    ) {
      setForm((f) => ({
        ...f,
        error:
          "Client ID, Client Secret, and Company ID (RealmId) are required."
      }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, error: null, testResult: null }));
    try {
      const resp = await api.messaging.post("QuickBooks/Configs", {
        displayName: form.displayName.trim() || "QuickBooks",
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        realmId: form.realmId.trim(),
        accessToken: form.accessToken.trim(),
        refreshToken: form.refreshToken.trim()
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setForm((f) => ({
          ...f,
          saving: false,
          error: (d as { error?: string }).error ?? "Failed to save."
        }));
        return;
      }
      setForm({
        displayName: "",
        clientId: "",
        clientSecret: "",
        realmId: "",
        accessToken: "",
        refreshToken: "",
        saving: false,
        testing: false,
        error: null,
        testResult: null
      });
      setShowAdd(false);
      onRefresh();
      ensureDefaultRoutingWorkflow(api.messaging);
    } catch (ex) {
      console.error("QuickBooks save failed", ex);
      setForm((f) => ({
        ...f,
        saving: false,
        error: "Network error — please try again."
      }));
    }
  }

  async function handleTest() {
    if (!form.clientId.trim()) {
      setForm((f) => ({ ...f, error: "Enter credentials first." }));
      return;
    }
    setForm((f) => ({ ...f, testing: true, testResult: null, error: null }));
    try {
      const resp = await api.messaging.post("QuickBooks/Test", {
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        realmId: form.realmId.trim(),
        accessToken: form.accessToken.trim(),
        refreshToken: form.refreshToken.trim()
      });
      const d = (await resp.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        companyName?: string;
      };
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: {
          success: d.success ?? false,
          message: d.success
            ? `Connected${d.companyName ? ` — ${d.companyName}` : ""}`
            : (d.error ?? "Connection test failed.")
        }
      }));
    } catch (ex) {
      console.error("QuickBooks test failed", ex);
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: { success: false, message: "Network error during test." }
      }));
    }
  }

  return (
    <>
      <SectionCard
        title="Connected Accounts"
        action={
          <PrimaryButton
            onClick={() => setShowAdd(!showAdd)}
            className="!px-3 !py-1.5 !text-xs"
            style={{ backgroundColor: QB_COLOR }}
          >
            + Add Account
          </PrimaryButton>
        }
      >
        {showAdd && (
          <SectionCard className="!rounded-xl mb-4 space-y-3 !bg-zinc-50">
            <div className="text-sm font-semibold text-zinc-700">
              Add QuickBooks Account
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">
                Display Name <span className="text-zinc-400">(optional)</span>
              </label>
              <FieldInput
                type="text"
                value={form.displayName}
                onChange={(e) => setField("displayName", e.target.value)}
                placeholder="e.g. My QuickBooks"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <FieldInput
                  type="text"
                  value={form.clientId}
                  onChange={(e) => setField("clientId", e.target.value)}
                  placeholder="From developer.intuit.com"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <FieldInput
                  type="password"
                  value={form.clientSecret}
                  onChange={(e) => setField("clientSecret", e.target.value)}
                  placeholder="Client Secret"
                  autoComplete="new-password"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Company ID (RealmId) <span className="text-red-500">*</span>
                </label>
                <FieldInput
                  type="text"
                  value={form.realmId}
                  onChange={(e) => setField("realmId", e.target.value)}
                  placeholder="e.g. 123456789012345"
                  className="font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Access Token
                </label>
                <FieldInput
                  type="password"
                  value={form.accessToken}
                  onChange={(e) => setField("accessToken", e.target.value)}
                  placeholder="OAuth access token"
                  autoComplete="new-password"
                  className="font-mono"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-zinc-600 mb-1">
                  Refresh Token
                </label>
                <FieldInput
                  type="password"
                  value={form.refreshToken}
                  onChange={(e) => setField("refreshToken", e.target.value)}
                  placeholder="OAuth refresh token"
                  autoComplete="new-password"
                  className="font-mono"
                />
              </div>
            </div>
            <FindKeyAccordion />
            {form.error && (
              <div
                className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800"
                role="alert"
              >
                {form.error}
              </div>
            )}
            {form.testResult && (
              <div
                className={`rounded-lg p-3 text-sm ${form.testResult.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
                role="status"
              >
                {form.testResult.message}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <SecondaryButton
                onClick={handleTest}
                disabled={form.testing || form.saving}
              >
                {form.testing ? "Testing…" : "Test connection"}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleSave}
                disabled={form.saving || form.testing}
              >
                {form.saving ? "Saving…" : "Save"}
              </PrimaryButton>
              <GhostButton
                onClick={() => {
                  setShowAdd(false);
                  setForm({
                    displayName: "",
                    clientId: "",
                    clientSecret: "",
                    realmId: "",
                    accessToken: "",
                    refreshToken: "",
                    saving: false,
                    testing: false,
                    error: null,
                    testResult: null
                  });
                }}
              >
                Cancel
              </GhostButton>
            </div>
          </SectionCard>
        )}

        {configs.length === 0 && !showAdd ? (
          <div className="text-center py-12 text-zinc-400">
            <div className="mb-3 flex justify-center">
              <FileText className="w-10 h-10 text-zinc-300" />
            </div>
            <div className="font-medium text-zinc-600 mb-1">
              No QuickBooks account connected
            </div>
            <p className="text-sm">
              Connect your QuickBooks credentials to enable AI-powered billing
              support in every conversation.
            </p>
            <PrimaryButton
              onClick={() => setShowAdd(true)}
              className="mt-4"
              style={{ backgroundColor: QB_COLOR }}
            >
              Connect QuickBooks →
            </PrimaryButton>
          </div>
        ) : (
          <div className="space-y-3">
            {configs.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                    style={{ backgroundColor: QB_COLOR }}
                  >
                    Q
                  </div>
                  <div>
                    <div className="font-medium text-zinc-800">
                      {c.displayName}
                    </div>
                    {c.companyName && (
                      <div className="text-xs text-zinc-500">
                        {c.companyName}
                      </div>
                    )}
                    <div className="text-xs text-zinc-400 font-mono">
                      Client ID: {c.clientIdMasked} · Realm: {c.realmId}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {c.enabled ? "Active" : "Disabled"}
                  </span>
                  <GhostButton
                    onClick={() => handleDelete(c.id)}
                    disabled={deleting === c.id}
                    className="!text-xs !text-red-500 hover:!text-red-700 hover:!no-underline"
                  >
                    {deleting === c.id ? "Removing..." : "Remove"}
                  </GhostButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {configs.length > 0 && (
        <SectionCard title="Transcript Auto-Push" className="mt-4">
          <div className="text-sm text-zinc-500 mb-4">
            When enabled, the full chat transcript is automatically attached as
            a note on the matched QuickBooks customer after every conversation
            ends.
          </div>
          {pushTranscriptsError && (
            <div
              className="mb-3 rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800"
              role="alert"
            >
              {pushTranscriptsError}
            </div>
          )}
          <div className="space-y-3">
            {configs.map((config) => (
              <div
                key={config.id}
                className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0"
              >
                <div>
                  <div className="text-sm font-medium text-zinc-700">
                    {config.displayName || config.companyName}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {config.pushTranscripts
                      ? "Transcript attached to customer after each conversation"
                      : "Transcript push is off for this connection"}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={config.pushTranscripts}
                  onClick={() => onTogglePushTranscripts(config)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                  aria-label={
                    config.pushTranscripts
                      ? "Disable transcript push"
                      : "Enable transcript push"
                  }
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </button>
              </div>
            ))}
          </div>
          {configs.some((c) => c.pushTranscripts && c.enabled) && (
            <div
              className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700"
              aria-live="polite"
            >
              Auto-push active — transcripts will appear as notes on the matched
              QuickBooks customer after each conversation.
            </div>
          )}
        </SectionCard>
      )}
    </>
  );
}

// ─── FieldReferenceTab ────────────────────────────────────────────────────────

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Customer");
  const scopeData =
    QB_FIELD_REFERENCE.find((s) => s.scope === activeScope) ??
    QB_FIELD_REFERENCE[0];
  return (
    <SectionCard
      title="Field Reference"
      description="Variable names for HTTP POST nodes to push QuickBooks data to your systems."
    >
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "customer_name":    "{quickbooks.customer.DisplayName}",
  "customer_email":   "{quickbooks.customer.PrimaryEmailAddr.Address}",
  "invoice_number":   "{quickbooks.invoice.DocNumber}",
  "invoice_total":    "{quickbooks.invoice.TotalAmt}",
  "amount_due":       "{quickbooks.invoice.Balance}",
  "due_date":         "{quickbooks.invoice.DueDate}"
}`}</CodeBlock>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {QB_FIELD_REFERENCE.map((s) => (
          <button
            key={s.scope}
            type="button"
            onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            style={activeScope === s.scope ? { backgroundColor: QB_COLOR } : {}}
          >
            {s.scope}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div
          className="px-4 py-2.5 text-white text-xs font-semibold"
          style={{ backgroundColor: QB_COLOR }}
        >
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Field
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Label
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Variable
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                    {f.field}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton
                      variable={`quickbooks.${scopeData.scopeKey}.${f.field}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function QuickBooks() {
  const api = useApi();
  const [configs, setConfigs] = useState<QuickBooksConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [pushTranscriptsError, setPushTranscriptsError] = useState<
    string | null
  >(null);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.messaging.get("QuickBooks/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("QuickBooks fetchConfigs failed", ex);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  async function togglePushTranscripts(config: QuickBooksConfig) {
    setPushTranscriptsError(null);
    const nextValue = !config.pushTranscripts;
    setConfigs((prev) =>
      prev.map((c) =>
        c.id === config.id ? { ...c, pushTranscripts: nextValue } : c
      )
    );
    try {
      const resp = await api.messaging.put(`QuickBooks/Configs/${config.id}`, {
        pushTranscripts: nextValue
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        setConfigs((prev) =>
          prev.map((c) =>
            c.id === config.id
              ? { ...c, pushTranscripts: config.pushTranscripts }
              : c
          )
        );
        setPushTranscriptsError(
          `Failed to update transcript push (${resp.status}): ${body || "no response body"}`
        );
        return;
      }
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, pushTranscripts: updated.pushTranscripts }
            : c
        )
      );
    } catch (ex) {
      console.error("QuickBooks togglePushTranscripts failed", ex);
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, pushTranscripts: config.pushTranscripts }
            : c
        )
      );
      setPushTranscriptsError(
        `Failed to update transcript push: ${ex instanceof Error ? ex.message : String(ex)}`
      );
    }
  }

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "accounts"}
        onClick={() => setActiveTab("accounts")}
      >
        Accounts
        {configs.length > 0 && (
          <span className="text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">
            {configs.length}
          </span>
        )}
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "ai-tools"}
        onClick={() => setActiveTab("ai-tools")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "field-reference"}
        onClick={() => setActiveTab("field-reference")}
      >
        Field Reference
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "data-mapping"}
        onClick={() => setActiveTab("data-mapping")}
      >
        Field Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "logs"}
        onClick={() => setActiveTab("logs")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="QuickBooks" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="quickbooks"
          onClose={() => setShowWizard(false)}
        />
      )}
      <CapabilitiesPanel />

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-blue-800 mb-0.5">
              Deploy the chat widget to your site
            </div>
            <div className="text-xs text-blue-600">
              Your QuickBooks integration is connected — grab your embed code
              from Deployments to go live.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/chat_window"
              className="text-xs px-3 py-1.5 rounded-lg border border-blue-300 bg-blue-50/60 text-blue-700 hover:bg-blue-100 transition"
            >
              Chat Window
            </Link>
            <Link
              to="/deployment"
              className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
            >
              Get Embed Code →
            </Link>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => setActiveTab("accounts")}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <AccountsTab
              configs={configs}
              onRefresh={fetchConfigs}
              onTogglePushTranscripts={togglePushTranscripts}
              pushTranscriptsError={pushTranscriptsError}
            />
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab
              hasConnection={hasActiveConfig}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "data-mapping" && (
            <SectionCard title="Field Mapping — QuickBooks">
              <QuickBooksDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <IntegrationActivityLog integrationSlug="QuickBooks" />
          )}
        </>
      )}
    </StickyRouteHeader>
  );
}
